import { usePdfGenerator } from '@/composables/usePdfGenerator';
import { toFixedPrice } from '@/composables/commonFunctions';
import { quoteIllustrationTemplate, CETA_LOGO_DATA_URI } from '@/templates/pdf/quoteIllustration';
import { getQuoteProductConfig } from '@/components/Quote/config/productRegistry';

// Builds the data for, and generates, the customer-facing "Quote Illustration"
// PDF shown in the confirmation page document list. Maps the selected scheme
// (premium, cover limits/excesses, endorsements) and the tenant's Statement of
// Fact into the quoteIllustration template, then hands off to the shared
// background-tab PDF generator.
// Official CETA logo (same CDN the scheme images come from).
const CETA_LOGO_URL = 'https://isistech.blob.core.windows.net/cdn/resources/CETA/logo.svg';

// TEMP (debugging): render the illustration as HTML in the /pdf.html tab instead
// of producing a PDF, so layout changes can be inspected with dev tools. Set to
// false to restore normal PDF output.
const DEBUG_RENDER_HTML = false;

// Stable keys for the price rows so callers can identify rows by key rather than
// by display label (labels vary per product/tenant). The premium/fee rows use
// these fixed keys; feature and addon rows use the prefixes below.
export const PRICE_ITEM_KEYS = {
  insurer: 'insurer',
  insurancePremium: 'insurancePremium',
  ipt: 'ipt',
  cetaFee: 'cetaFee',
  brokerFee: 'brokerFee',
  totalCost: 'totalCost',
  quoteReference: 'quoteReference',
} as const;

export const FEATURE_PRICE_KEY_PREFIX = 'feature:';
export const ADDON_PRICE_KEY_PREFIX = 'addon:';

// A row in the Statement Of Price grid (page and PDF). `sublabel` sits beside the
// label ("(Including IPT)"); `subvalue` is a highlighted line under the value
// ("Max £2,500 pm"). A tenant's buildCoverItems returns these.
export type PriceItem = {
  key: string;
  label: string;
  sublabel?: string;
  value: string;
  subvalue?: string;
};

const normalise = (value: any): string =>
  String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Addons live in scheme.Addons / scheme.GroupedAddons[].GroupAddons — NOT in
// Features.
const allSchemeAddons = (scheme: any): any[] => [
  ...(scheme?.Addons ?? []),
  ...((scheme?.GroupedAddons ?? []).flatMap((group: any) => group?.GroupAddons ?? [])),
];

export function useQuoteIllustration()
{
  const { generateInBackgroundTab } = usePdfGenerator();

  // Fetch the official logo and inline it as an SVG data URI. Inlining (rather
  // than pointing <img> at the remote URL) means the background-tab renderer's
  // rasterizeSvgImages converts it to PNG and html2canvas never sees a
  // cross-origin image that would taint the canvas. Falls back to the bundled
  // inline mark if the fetch is blocked (CORS/offline).
  const loadLogo = async (): Promise<string> =>
  {
    try
    {
      const response = await fetch(CETA_LOGO_URL);
      if (!response.ok) return CETA_LOGO_DATA_URI;
      const svg = await response.text();
      return `data:image/svg+xml,${encodeURIComponent(svg)}`;
    }
    catch
    {
      return CETA_LOGO_DATA_URI;
    }
  };

  // Premium, fees and the quote reference. Always machine-built from the scheme's
  // AnnualPremium — a tenant's cover rows replace the feature rows below, never
  // these.
  const buildPremiumItems = (scheme: any): PriceItem[] =>
  {
    const annualPremium = scheme?.AnnualPremium ?? {};

    return [
      { key: PRICE_ITEM_KEYS.insurer, label: 'Insurer', value: scheme?.Insurer?.Description ?? scheme?.Insurer?.Name ?? '' },
      { key: PRICE_ITEM_KEYS.insurancePremium, label: 'Insurance Premium', sublabel: '(Including IPT)', value: toFixedPrice(annualPremium.GrossPremium) },
      { key: PRICE_ITEM_KEYS.ipt, label: 'Insurance Premium Tax (IPT)', value: toFixedPrice(annualPremium.Ipt) },
      { key: PRICE_ITEM_KEYS.cetaFee, label: 'CETA Fee', value: toFixedPrice(annualPremium.TotalFee) },
      { key: PRICE_ITEM_KEYS.brokerFee, label: 'Broker Fee', value: toFixedPrice(annualPremium.AgentAdminFee ?? 0) },
      { key: PRICE_ITEM_KEYS.totalCost, label: 'Total Annual Customer Cost', sublabel: '(Including IPT and all applicable fees)', value: toFixedPrice(annualPremium.TotalPayable) },
      { key: PRICE_ITEM_KEYS.quoteReference, label: 'Quote Reference', value: scheme?.QuoteReference ?? '' },
    ];
  };

  // Cover limits and excesses straight from the scheme's Features dictionary
  // ({ Name, Value } pairs). Used for any product that doesn't curate its own
  // cover rows (see buildCoverItems).
  const buildFeatureItems = (scheme: any): PriceItem[] =>
  {
    // Some schemes also surface addons as Feature rows; keep those out of the
    // Features dump (matched against every addon's Code/Name) so the only addons
    // shown are the selected ones buildAddonItems adds.
    const addonIds = allSchemeAddons(scheme)
      .flatMap((addon: any) => [addon?.Code, addon?.Name, addon?.CustomerFacingName])
      .map(normalise)
      .filter(Boolean);
    const matchesAnyAddon = (featureKey: string, featureName: any): boolean =>
    {
      const key = normalise(featureKey);
      const name = normalise(featureName);
      return addonIds.some((id) =>
        id === key || id === name || (id.length >= 5 && (name.includes(id) || key.includes(id))));
    };

    return Object.entries(scheme?.Features ?? {}).reduce<PriceItem[]>(
      (items, [featureKey, feature]: [string, any]) =>
      {
        if (!feature || feature.Name == null) return items;
        if (matchesAnyAddon(featureKey, feature.Name)) return items;

        // A cover that isn't included reads as "No" rather than a blank value.
        const value = feature.IsIncluded === false
          ? 'No'
          : (feature.Value != null ? String(feature.Value) : '');

        items.push({ key: `${FEATURE_PRICE_KEY_PREFIX}${normalise(featureKey)}`, label: String(feature.Name), value });
        return items;
      },
      [],
    );
  };

  // The customer's chosen addons, mirroring the on-screen premium summary
  // (name + price).
  const buildAddonItems = (scheme: any): PriceItem[] =>
    allSchemeAddons(scheme)
      .filter((addon: any) => addon?.IsSelected && !addon?.IsIncluded)
      .reduce<PriceItem[]>((items, addon: any) =>
      {
        const name = addon?.Name ?? addon?.CustomerFacingName;
        if (!name) return items;

        const price = addon?.OriginalTotalPayable != null ? toFixedPrice(addon.OriginalTotalPayable) : '';
        items.push({ key: `${ADDON_PRICE_KEY_PREFIX}${normalise(addon?.Code ?? name)}`, label: String(name), value: price });
        return items;
      }, []);

  // A product's own cover rows, if its schema exports buildCoverItems. Returning
  // null means "not configured", so callers fall back to the Features dump and
  // every other product behaves exactly as before.
  const loadTenantCoverItems = async (scheme: any, productId: string): Promise<PriceItem[] | null> =>
  {
    const config = getQuoteProductConfig(productId);
    if (!config) return null;

    try
    {
      const tenantModule = await config.loadSchema();
      return tenantModule.default?.buildCoverItems?.(scheme) ?? null;
    }
    catch (error)
    {
      console.error('Failed to load tenant cover items:', error);
      return null;
    }
  };

  // The "Statement Of Price - Cover Details" rows for the confirmation page: the
  // quote reference, then the product's curated cover rows or the generic
  // Features dump. The premium/fee rows and endorsements already appear in the
  // cards above the grid, and addons in the premium summary, so they are left out.
  const buildCoverItems = async (scheme: any, productId: string): Promise<PriceItem[]> =>
  {
    const tenantItems = await loadTenantCoverItems(scheme, productId);

    const quoteReference = buildPremiumItems(scheme)
      .filter((item) => item.key === PRICE_ITEM_KEYS.quoteReference);

    const coverItems = tenantItems
      ?? buildFeatureItems(scheme).filter((item) => !item.key.includes('endorsement'));

    return [...quoteReference, ...coverItems];
  };

  // html2canvas (used by jsPDF.html) mis-positions inline bold runs, dropping
  // them below their line. Strip <b>/<strong> tags (keeping their text) so the
  // content renders as a single, correctly-aligned weight.
  const stripBoldTags = (html: string): string =>
    String(html ?? '').replace(/<\s*\/?\s*(?:b|strong)(?:\s[^>]*)?>/gi, '');

  const buildEndorsements = (scheme: any): Array<{ summary: string; content: string }> =>
    (scheme?.Covers?.[0]?.Endorsements ?? []).map((endorsement: any) => ({
      summary: endorsement?.Summary ?? '',
      content: stripBoldTags(endorsement?.Content ?? ''),
    }));

  const buildStatementOfFact = async (riskData: any, productId: string): Promise<Array<{ title: string; items: any[] }>> =>
  {
    const config = getQuoteProductConfig(productId);
    if (!config) return [];

    try
    {
      // Mirrors Factory.vue's dynamic tenant import; already cached from the
      // journey so this resolves instantly.
      const tenantModule = await config.loadSchema();
      return tenantModule.default?.buildStatementOfFact?.(riskData) ?? [];
    }
    catch (error)
    {
      console.error('Failed to build Statement of Fact for illustration:', error);
      return [];
    }
  };

  // The tenant's allowlist of documents to show on the confirmation step (see
  // confirmationDocuments in the tenant schema). Empty when unconfigured.
  const getConfirmationDocuments = async (productId: string): Promise<string[]> =>
  {
    const config = getQuoteProductConfig(productId);
    if (!config) return [];

    try
    {
      const tenantModule = await config.loadSchema();
      return tenantModule.default?.confirmationDocuments ?? [];
    }
    catch (error)
    {
      console.error('Failed to load confirmation document config:', error);
      return [];
    }
  };

  // The illustration's Statement Of Price: premium/fee rows, then the product's
  // curated cover rows (or the Features dump), then the selected addons — so the
  // PDF shows the same cover wording as the confirmation page and quote page.
  const buildIllustrationPriceItems = async (scheme: any, productId: string): Promise<PriceItem[]> =>
  {
    const tenantItems = await loadTenantCoverItems(scheme, productId);

    return [
      ...buildPremiumItems(scheme),
      ...(tenantItems ?? buildFeatureItems(scheme)),
      ...buildAddonItems(scheme),
    ];
  };

  const buildData = async (scheme: any, riskData: any, productId: string) => ({
    brand: 'CETA',
    logo: await loadLogo(),
    reference: scheme?.QuoteReference ?? '',
    // Friendly insurer/product name (e.g. "Covea Household"), matching the
    // on-screen premium card — not the internal scheme code (scheme.Name).
    productName: scheme?.Insurer?.Description ?? scheme?.Insurer?.Name ?? scheme?.SchemeDescription ?? '',
    priceItems: await buildIllustrationPriceItems(scheme, productId),
    endorsements: buildEndorsements(scheme),
    sofSections: await buildStatementOfFact(riskData, productId),
  });

  const generate = async (
    scheme: any,
    riskData: any,
    productId: string,
    filename?: string,
  ): Promise<void> =>
  {
    const data = await buildData(scheme, riskData, productId);
    // e.g. QuoteIllustration-HOHO1000-Q-038.pdf. The quote reference contains
    // slashes, which aren't filename-legal, so collapse any run of unsafe
    // characters to a single hyphen.
    const reference = String(scheme?.QuoteReference ?? '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const resolvedName =
      filename ?? (reference ? `QuoteIllustration-${reference}.pdf` : 'QuoteIllustration.pdf');
    // TEMP (debugging): render the illustration as HTML in the /pdf.html tab
    // instead of producing a PDF, so layout changes can be inspected with dev
    // tools. Set back to false (or drop the arg) to restore normal PDF output.
    await generateInBackgroundTab(quoteIllustrationTemplate, data, resolvedName, DEBUG_RENDER_HTML);
  };

  return { generate, buildData, buildStatementOfFact, buildCoverItems, getConfirmationDocuments };
}
